import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MainTabsNav from '../components/MainTabsNav';

/**
 * 소개 > 입점 교육 기관 / 입점 채용 기관 목록.
 * 목업 `[소메뉴] 입점 기관`(검색 + 분류 + 정렬 + 표 + 더 보기)을 그대로 이식.
 * 공개 화면이라 서버도 기관명·분류·입점일만 내려준다.
 */

type DirectoryKind = 'education' | 'recruitment';

interface DirectoryRow {
  id: string;
  name: string;
  /** 교육 기관은 업종(피트니스/필라테스), 채용 기관은 지역(시/도) */
  group: string;
  joinedAt: string;
}

interface RawRow {
  id: string;
  name: string;
  category?: string;
  region?: string;
  joinedAt: string;
}

const PAGE_SIZE = 9;

const CONFIG: Record<DirectoryKind, {
  eyebrow: string;
  title: string;
  lead: string;
  endpoint: string;
  searchPlaceholder: string;
  /** 두 번째 열 헤더 + 필터 라벨 */
  groupLabel: string;
  nameLabel: string;
  emptyAll: string;
}> = {
  education: {
    eyebrow: 'Education Partners',
    title: '입점 교육 기관',
    lead: '기관명이나 관심 분야로 찾아보세요.',
    endpoint: '/api/v1/providers/directory',
    searchPlaceholder: '교육 기관명 검색',
    groupLabel: '업종 카테고리',
    nameLabel: '교육 기관명',
    emptyAll: '아직 입점한 교육 기관이 없습니다.',
  },
  recruitment: {
    eyebrow: 'Recruitment Partners',
    title: '입점 채용 기관',
    lead: '기관명이나 지역으로 찾아보세요.',
    endpoint: '/api/v1/employers/directory',
    searchPlaceholder: '채용 기관명 검색',
    groupLabel: '지역',
    nameLabel: '채용 기관명',
    emptyAll: '아직 입점한 채용 기관이 없습니다.',
  },
};

export default function PartnerDirectoryPage({ kind }: { kind: DirectoryKind }) {
  const config = CONFIG[kind];
  const [rows, setRows] = useState<DirectoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const [sort, setSort] = useState<'name' | 'latest'>('name');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    fetch(config.endpoint)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: { institutions?: RawRow[] }) => {
        if (cancelled) return;
        const list = (data.institutions || []).map((row) => ({
          id: row.id,
          name: row.name,
          group: (kind === 'education' ? row.category : row.region) || '',
          joinedAt: row.joinedAt || '',
        }));
        setRows(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Partner directory:', err);
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [config.endpoint, kind]);

  // 필터 드롭다운 옵션은 실제 데이터에서 뽑는다
  const groupOptions = useMemo(
    () => [...new Set(rows.map((row) => row.group).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ko')),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('ko');
    return rows
      .filter((row) => (!q || row.name.toLocaleLowerCase('ko').includes(q)) && (!group || row.group === group))
      .sort((a, b) =>
        sort === 'latest' ? b.joinedAt.localeCompare(a.joinedAt) : a.name.localeCompare(b.name, 'ko')
      );
  }, [rows, query, group, sort]);

  // 조건이 바뀌면 다시 첫 페이지부터
  useEffect(() => setVisible(PAGE_SIZE), [query, group, sort]);

  const hasFilter = Boolean(query.trim() || group);

  return (
    <div className="flex-1 bg-gray-50 flex flex-col">
      <Helmet>
        <title>{`${config.title} - StampFit`}</title>
        <meta name="description" content={`스탬핏에 입점한 ${config.title} 목록입니다.`} />
      </Helmet>

      <Header />
      <MainTabsNav className="mt-16" />

      <main className="flex-1 w-full max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 pb-24">
        {/* 제목 + 안내 문구 */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 pt-8">
          <div>
            <p className="text-[0.72rem] font-extrabold tracking-[0.13em] text-accent">{config.eyebrow}</p>
            <h1 className="mt-2 flex items-baseline gap-2 text-[clamp(1.65rem,2.2vw,2.25rem)] font-bold tracking-[-0.035em] text-gray-900">
              {config.title}
              {!loading && !loadError && (
                <span className="text-[1.05rem] font-semibold text-accent">{filtered.length}곳</span>
              )}
            </h1>
          </div>
          <p className="text-sm text-gray-500">{config.lead}</p>
        </div>

        {/* 검색 / 분류 / 정렬 */}
        <div className="flex flex-wrap gap-2.5 mt-7 mb-5">
          <label className="flex items-center gap-2.5 flex-1 min-w-[240px] max-w-[480px] h-[46px] px-3.5 rounded-lg border border-gray-200 bg-white">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={config.searchPlaceholder}
              autoComplete="off"
              className="w-full text-sm outline-none bg-transparent"
            />
          </label>
          <select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            aria-label={config.groupLabel}
            className="h-[46px] px-3.5 pr-8 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">{`${config.groupLabel} 전체`}</option>
            {groupOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as 'name' | 'latest')}
            aria-label="정렬 기준"
            className="h-[46px] px-3.5 pr-8 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="name">가나다순</option>
            <option value="latest">최근 입점순</option>
          </select>
        </div>

        {/* 목록 */}
        {loading ? (
          <p className="text-center text-gray-500 py-16 bg-white border border-gray-200 rounded-xl">
            목록을 불러오는 중입니다...
          </p>
        ) : loadError ? (
          <p className="text-center text-gray-500 py-16 bg-white border border-gray-200 rounded-xl">
            기관 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-gray-500 py-16 bg-white border border-gray-200 rounded-xl">
            {hasFilter
              ? `검색 결과가 없습니다. 다른 검색어 또는 ${config.groupLabel}을(를) 선택해 주세요.`
              : config.emptyAll}
          </p>
        ) : (
          <>
            <div className="overflow-hidden bg-white border border-gray-200 rounded-xl">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th scope="col" className="w-[55%] text-left text-[13px] font-bold text-slate-600 px-6 py-4">
                      {config.nameLabel}
                    </th>
                    <th scope="col" className="w-[25%] text-left text-[13px] font-bold text-slate-600 px-6 py-4">
                      {config.groupLabel}
                    </th>
                    <th scope="col" className="text-left text-[13px] font-bold text-slate-600 px-6 py-4">
                      입점일
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, visible).map((row) => (
                    <tr key={row.id} className="border-t border-gray-100 hover:bg-slate-50/70 transition-colors">
                      <td className="px-6 py-4 text-[15px] font-bold text-gray-900 truncate">{row.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{row.group || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{row.joinedAt || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filtered.length > visible && (
              <div className="text-center mt-7">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="px-10 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-700 hover:border-accent hover:text-accent transition-colors"
                >
                  더 보기
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
